/**
 * Pulaar — AuthContext
 * Unified state for email/password + Emergent-managed Google sign-in.
 *
 * The cookie `session_token` is httpOnly so we cannot read it from JS.
 * Source of truth: `GET /api/auth/me` (server validates the cookie).
 *
 * Race-condition note (Emergent OAuth):
 *   On the Google return, the URL hash contains `session_id=...`.
 *   AuthCallback exchanges it for our session cookie. Until that exchange
 *   succeeds, calling /me would 401 — so we SKIP the initial /me check while
 *   the hash carries a session_id and let AuthCallback set the user.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Always send cookies on auth calls
const http = axios.create({ baseURL: API, withCredentials: true });

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = signed in
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const register = useCallback(async (payload) => {
    const { data } = await http.post("/auth/register", payload);
    setUser(data);
    return data;
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const { data } = await http.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/mon-carnet";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const exchangeEmergentSession = useCallback(async (sessionId) => {
    const { data } = await http.post("/auth/emergent-session", { session_id: sessionId });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await http.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
  }, []);

  const updateMe = useCallback(async (payload) => {
    const { data } = await http.patch("/auth/me", payload);
    setUser(data);
    return data;
  }, []);

  const value = {
    user: user || null,
    isAuthenticated: !!user && user !== false,
    loading,
    register,
    login,
    loginWithGoogle,
    exchangeEmergentSession,
    logout,
    updateMe,
    refresh: checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
